import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["aria-atomic", "aria-live", "className", "error", "fwdRef", "icon", "success"];
import React, { forwardRef, useContext, useEffect, useState } from 'react';
import SmoothCollapse from 'react-smooth-collapse';
import { IconError, IconSuccess } from '@op/opds.opux-icons/indicator';
import { buildVersionedClassNames } from '@op/opds.opux-utilities';
import { usePrevious } from '../common/hooks';
import { useLocale } from '../common/locale';
import { FormContext } from '../form/form';
import messages from '../text-input/_messages';
const CLOSE_DELAY = 1000;

// https://github.com/facebook/react/issues/11538#issuecomment-390386520
const ErrorWrapper = 'span';

/**
 * _An error message displayed with invalid inputs that provides help to users._
 *
 * ***
 *
 * A form input error message component that is used with invalid inputs.
 * The component is internally used by the [ValidInput](#/Forms/ValidInput).
 *
 * The message displayed is set with the `error` prop. Changing prop to
 * an empty value will show a success icon and animate component’s
 * height to zero.
 *
 * Additional props (including `ref`) will be forwarded to the wrapping `<div>` element.
 */
export const InputError = _ref => {
  let {
      'aria-atomic': ariaAtomic = true,
      'aria-live': ariaLive = 'off',
      className,
      error,
      fwdRef,
      icon: Icon = IconError,
      success = true
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const lc = useLocale();
  const [scheduledToClose, setScheduledToClose] = useState(false);
  const prevError = usePrevious(error);
  const {
    spacing
  } = useContext(FormContext);
  const visible = scheduledToClose || !!prevError || !!error;
  const cn = buildVersionedClassNames('ds-input-error', className, {
    'ds-input-error--hero': spacing === 'hero'
  });

  // This is the default transition of SmoothCollapse with a small delay to ensure
  // a click target below the field receives the focus before the error is animated.
  // https://oppjira.eficode.com/browse/OPDS-2183
  const heightTransition = '.25s ease .1s';
  useEffect(() => {
    if (prevError && !error) {
      setScheduledToClose(true);
      const timer = setTimeout(() => setScheduledToClose(false), CLOSE_DELAY);
      return () => clearTimeout(timer);
    }
  }, [error]);
  return /*#__PURE__*/React.createElement(SmoothCollapse, {
    eagerRender: true,
    expanded: visible,
    heightTransition: heightTransition
  }, /*#__PURE__*/React.createElement("div", _extends({
    "aria-atomic": ariaAtomic,
    "aria-live": ariaLive
  }, props, {
    className: cn,
    ref: fwdRef
  }), /*#__PURE__*/React.createElement("div", null, error ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Icon, {
    color: "negative",
    label: messages[lc].error + ": ",
    size: "small"
  }), /*#__PURE__*/React.createElement(ErrorWrapper, null, error instanceof Error ? error.message : error)) : success && /*#__PURE__*/React.createElement(IconSuccess, {
    color: "positive",
    size: "small"
  }))));
};
const Fwd = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/React.createElement(InputError, _extends({}, props, {
  fwdRef: ref
})));
Fwd.displayName = 'InputError';
export default Fwd;